import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, take, tap } from 'rxjs/operators';

import {
  CUSTOM_FIELD_TARGET,
  CustomFieldDefinition,
  CustomFieldDefinitionCreate,
  CustomFieldDefinitionUpdate,
  GenericWrapper,
  GenericWrapperArray,
} from '../models/main';
import { MainCacheService } from '../shared/main-chache.service';
import { MainEventService } from '../shared/main-event.service';
import { ApiService } from './api.service';
import { FormatHttp } from './format-api-responses';

/**
 * Catalogo de definiciones de custom fields de la organizacion (`/custom-field-definitions`).
 *
 * Las lecturas se cachean durante la sesion porque el componente de custom fields se recrea a menudo (p. ej. cada vez
 * que se despliega una fila de mercancia). Cualquier escritura desde este servicio vacia la cache, y el login la vacia
 * entera porque el servicio sobrevive al cambio de usuario. Los fallos no se cachean.
 */
@Injectable({ providedIn: 'root' })
export class CustomFieldDefinitionsService {

  private cache = new Map<string, Observable<any>>();

  constructor(private http: HttpClient, private api: ApiService, private mainCache: MainCacheService) {
    MainEventService.login.subscribe(() => this.cache.clear());
  }

  private get endpoint(): string {
    return `${this.api.endpoint}/custom-field-definitions`;
  }

  /** Definiciones de la organizacion del usuario. Solo para sus miembros; la escritura es solo para administradores */
  list(filter: { target?: CUSTOM_FIELD_TARGET, archived?: boolean } = {}): Observable<CustomFieldDefinition[]> {
    let params = new HttpParams();
    if (filter.target) params = params.set('filter.target', filter.target);
    if (filter.archived !== undefined) params = params.set('filter.archived', filter.archived);
    return this.http.get<GenericWrapperArray<CustomFieldDefinition>>(this.endpoint, { params }).pipe(
      map(res => CustomFieldDefinitionsService.sort(FormatHttp.formatResponseArray(res))),
      catchError(err => this.api.handleError(err)),
    );
  }

  /** Definiciones no archivadas aplicables a los custom fields de un expediente (las de su organizacion) */
  listForRecord(recordId: string, target: CUSTOM_FIELD_TARGET): Observable<CustomFieldDefinition[]> {
    return this.cached(`record:${recordId}:${target}`, () => this.http.get<GenericWrapperArray<CustomFieldDefinition>>(
      `${this.api.endpoint}/records/${recordId}/custom-field-definitions`, { params: new HttpParams().set('filter.target', target) },
    ).pipe(
      map(res => CustomFieldDefinitionsService.sort(FormatHttp.formatResponseArray(res))),
    ), []);
  }

  /**
   * Definiciones archivadas de la organizacion de un expediente, para mostrar la etiqueta y el icono de archivado de los
   * valores que aun apuntan a ellas. Las ve cualquiera que pueda leer el expediente, tambien los invitados.
   */
  listArchivedForRecord(recordId: string, target: CUSTOM_FIELD_TARGET): Observable<CustomFieldDefinition[]> {
    return this.cached(`archived:${recordId}:${target}`, () => this.http.get<GenericWrapperArray<CustomFieldDefinition>>(
      `${this.api.endpoint}/records/${recordId}/custom-field-definitions`,
      { params: new HttpParams().set('filter.target', target).set('filter.archived', true) },
    ).pipe(
      map(res => FormatHttp.formatResponseArray(res)),
    ), []);
  }

  /** Si el usuario puede crear definiciones que valgan para el expediente: administrador de la organizacion del expediente */
  canManageForRecord(recordId: string): Observable<boolean> {
    // Peticion directa, sin el manejo de errores de ApiService: un fallo solo debe ocultar la opcion, sin toast ni redireccion
    return this.cached(`canManage:${recordId}`, () => combineLatest([
      this.mainCache.getUser().pipe(take(1)),
      this.http.get<GenericWrapper<{ organization?: { id: string } }>>(`${this.api.endpoint}/records/${recordId}`),
    ]).pipe(
      map(([user, record]) => !!user?.isAdmin && !!user.organization && user.organization.id === record.data.attributes.organization?.id),
    ), false);
  }

  create(data: CustomFieldDefinitionCreate): Observable<CustomFieldDefinition> {
    return this.http.post<GenericWrapper<CustomFieldDefinition>>(this.endpoint, this.api.formatData(data)).pipe(
      map(res => FormatHttp.fixJSONAPI(res.data)),
      tap(() => this.clearCache()),
      catchError(err => this.api.handleError(err)),
    );
  }

  /** Edita o archiva (`archived`). `target`, `key` y `dataType` no se pueden cambiar */
  update(id: string, data: CustomFieldDefinitionUpdate): Observable<CustomFieldDefinition> {
    return this.http.patch<GenericWrapper<CustomFieldDefinition>>(`${this.endpoint}/${id}`, this.api.formatData(data)).pipe(
      map(res => FormatHttp.fixJSONAPI(res.data)),
      tap(() => this.clearCache()),
      catchError(err => this.api.handleError(err)),
    );
  }

  /** La cache de permisos se conserva: no depende del catalogo */
  clearCache(): void {
    [...this.cache.keys()].filter(key => !key.startsWith('canManage:')).forEach(key => this.cache.delete(key));
  }

  static sort(definitions: CustomFieldDefinition[]): CustomFieldDefinition[] {
    return [...definitions].sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  }

  /**
   * Comparte la peticion entre los componentes que la piden a la vez. Si falla devuelve `fallback` y la quita de la cache
   * para reintentarla en la siguiente consulta: sin catalogo el componente sigue funcionando con campos libres.
   */
  private cached<T>(key: string, factory: () => Observable<T>, fallback: T): Observable<T> {
    if (!this.cache.has(key)) {
      this.cache.set(key, factory().pipe(
        catchError(() => {
          this.cache.delete(key);
          return of(fallback);
        }),
        shareReplay(1),
      ));
    }
    return this.cache.get(key)!;
  }
}
